import React from 'react'
import { Button, Heading, Paragraph } from '~elements'
import { EventDateProps, RichTextType } from '~types'
import dayjs from 'utils/dayjs'

interface CalendarModalContentProps {
  ctaBuyText: string
  eventInfo: EventDateProps
  ctaSoldOutText: RichTextType
  showPriceInModal?: boolean
}

const CalendarModalContent = ({
  ctaBuyText = 'Buy Tickets',
  ctaSoldOutText = { html: 'Sold Out' },
  eventInfo,
  showPriceInModal
}: CalendarModalContentProps) => {
  const {
    category,
    date_time,
    event_type,
    price,
    host_url,
    id,
    formatted_start_time,
    is_sold_out,
    hidePricing,
    spanMultipleDays,
    end_date
  } = eventInfo || {}
  const { min: minPrice, max: maxPrice } = price || {}
  const eventPriceRange =
    typeof minPrice === 'number' && typeof maxPrice === 'number'
      ? minPrice === maxPrice
        ? `$${minPrice.toFixed(2)}`
        : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
      : ''
  const localeTimestamp = dayjs(date_time)
  const endTimestamp = dayjs.utc(end_date)
  const formattedEventDay = localeTimestamp.format('dddd')
  const formattedEndDay = dayjs.utc(end_date).format('dddd')
  const formattedEventDate = localeTimestamp.format('MMMM DD, YYYY')
  const shouldShowPrices = showPriceInModal && !is_sold_out && !hidePricing && eventPriceRange

  let dateString
  if (localeTimestamp.isSame(endTimestamp, 'month') && localeTimestamp.isSame(endTimestamp, 'year')) {
    dateString = `${localeTimestamp.format('MMMM')} ${localeTimestamp.format('DD')} - ${endTimestamp.format(
      'DD'
    )}, ${localeTimestamp.format('YYYY')}`
  } else if (localeTimestamp.isSame(endTimestamp, 'year')) {
    dateString = `${localeTimestamp.format('MMMM')} ${localeTimestamp.format('DD')} - ${endTimestamp.format(
      'MMMM'
    )} ${endTimestamp.format('DD')}, ${localeTimestamp.format('YYYY')}`
  } else {
    dateString = `${localeTimestamp.format('MMMM DD, YYYY')} - ${endTimestamp.format('MMMM DD, YYYY')}`
  }

  const getCtaUrl = () => {
    if (host_url) {
      const url = new URL(host_url)

      url.searchParams.append('utm_source', 'sphere')
      url.searchParams.append('utm_medium', 'web')

      url.searchParams.append('camefrom', 'CFC_SPHERE')

      return url.toString()
    } else {
      return host_url
    }
  }

  return (
    <>
      <Heading level={6}>Showtime selected</Heading>
      <Heading level={2} noMarginTop={true} noMarginBottom={true}>
        {formattedEventDay}
        {spanMultipleDays ? `- ${formattedEndDay}` : ''}
        <br />
        {spanMultipleDays ? dateString : formattedEventDate}
        <br />
        {formatted_start_time} PST
      </Heading>
      {shouldShowPrices && (
        <Paragraph
          text={{ html: `<p>Tickets from ${eventPriceRange}<sup>*</sup></p>` }}
          justify='center'
          fontSize='md'
          noMarginBottom={true}
        />
      )}
      <Paragraph
        text={{ html: '<p>Ticket purchase available through Ticketmaster.</p>' }}
        justify='center'
        fontSize='md'
        noMarginBottom={true}
      />

      <Button
        color='primary'
        fill='solid'
        isTargetBlank
        justify='center'
        size='md'
        title={ctaBuyText}
        url={getCtaUrl()}
        analytics={{
          linkLocation: 'modal',
          linkModule: 'calendar_modal',
          eventData: {
            eventCategory: category && category.length > 0 ? category[0]?.value : '',
            eventType: event_type && event_type.length > 0 ? event_type[0]?.value : '',
            eventDate: dayjs(eventInfo?.date_time).format('MM/DD/YYYY'),
            ticketmasterEventId: id
          }
        }}
      />
    </>
  )
}

export default CalendarModalContent
